import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { getApprovedWorksByType } from "../../services/AppService";
import closeButton from "../../assets/images/close.png";
import { useTranslation } from "react-i18next";
import PageFade from "../../components/Login/PageFade";

const PaymentWorkOrderList = () => {
    const { workTypeId } = useParams(); // Captured from URL
    const { state } = useLocation();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

   useEffect(() => {
        const delayDebounce = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPage(0); // Always reset to page 1 when searching
        }, 500);
        return () => clearTimeout(delayDebounce);
    }, [searchTerm]);

    const fetchWorks = useCallback(async () => {
        if (!workTypeId || workTypeId === "undefined") return;
        setLoading(true);
        try {
            const res = await getApprovedWorksByType(workTypeId,debouncedSearch, page, size);
            if (res) {
                setData(res.content || []);
                setTotalPages(res.totalPages || 0);
                setTotalElements(res.totalElements || 0);
            }
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, [workTypeId, debouncedSearch, page, size]);

    useEffect(() => { fetchWorks(); }, [fetchWorks]);

    return (
        <PageFade>
            <div className="bg-white shadow-lg rounded-2xl p-6">
                <div className="flex justify-between items-center border-b pb-3 mb-4">
                    <h4 className="text-xl font-semibold text-blue uppercase">
                        {t('pageTitles.paymentList') || "Add / Edit Payment"} : {state?.workTypeName || ""}
                    </h4>
                    <img src={closeButton} alt="Close" className="w-6 h-6 cursor-pointer" onClick={() => navigate(-1)} />
                </div>

                <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                    <div className="flex items-center gap-2">
                        <label className="text-sm font-medium">{t('common.show')}:</label>
                        <select
                            value={size}
                            onChange={(e) => {
                                setSize(Number(e.target.value));
                                setPage(0);
                            }}
                            className="border rounded px-2 py-1 text-sm outline-none focus:border-blue-400"
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                        </select>
                        <label className="text-sm font-medium">{t('common.entries')}</label>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            placeholder={t('common.search')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="border rounded px-3 py-1 text-sm outline-none focus:border-blue-400 w-full sm:w-64 shadow-sm"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="w-full border  ctable">
                        <thead className="bg-[#a0adf1] text-white">
                            <tr>
                                <th className="px-2 py-2 text-center border-b border-black">{t('tables.srNo')}</th>
                                <th className="px-2 py-2 text-left border-b border-black">{t('work.workName')}</th>
                                <th className="px-2 py-2 text-left border-b border-black">{t('work.tenderId')}</th>
                                <th className="px-2 py-2 text-left border-b border-black">{t('work.bidderName')}</th>
                                <th className="px-2 py-2 text-right border-b border-black">{t('work.bidAmount')}</th>
                                <th className="px-2 py-2 text-left border-b border-black">{t('work.workOrderNo')}</th>
                                <th className="px-2 py-2 text-center border-b border-black w-16">{t('work.duration')}</th>
                                <th className="px-2 py-2 text-right border-b border-black font-bold">{t('accounts.payment.paidAmount')}</th>
                                <th className="px-2 py-2 text-right border-b border-black font-bold">{t('accounts.payment.balanceAmount')}</th>
                                <th className="px-2 py-2 text-center border-b border-black">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((item, idx) => {
                                // Logic from image_845ff7.png
                                const paidAmount = item.totalPaidAmount || 0;
                                const balance = item.bidAmount - paidAmount;

                                return (
                                    <tr key={item.id} className="hover:bg-blue-50 transition-colors border-b">
                                        <td className="px-2 py-3 text-center border-r">{page * size + idx + 1}</td>
                                        <td className="px-2 py-3 border-r font-medium uppercase">{item.workTitle}</td>
                                        <td className="px-2 py-3 border-r">{item.tenderId}</td>
                                        <td className="px-2 py-3 border-r">{item.bidderName}</td>
                                        <td className="px-2 py-3 border-r text-right font-semibold">{item.bidAmount?.toLocaleString('en-IN')}</td>
                                        <td className="px-2 py-3 border-r">{item.workOrderNo}</td>
                                        <td className="px-2 py-3 border-r text-center">{item.duration}</td>
                                        <td className="px-2 py-3 border-r text-right text-blue-700 font-bold">{paidAmount.toLocaleString('en-IN')}</td>
                                        <td className={`px-2 py-3 border-r text-right font-extrabold ${balance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                            {balance.toLocaleString('en-IN')}
                                        </td>
                                        <td className="px-2 py-3 text-center">
                                            <button
                                                onClick={() => navigate(`/smartcity/accounts/payment/add-payment/${item.id}`, { state: { workTitle: item.workTitle } })}
                                                className="staff-btn-save"
                                            >
                                                Payment
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Section */}
                <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm font-medium">
                    <p className="text-gray-600">
                        {t('common.showing')} {data.length > 0 ? page * size + 1 : 0} {t('common.to')} {page * size + data.length} {t('common.of')} {totalElements} {t('common.entries')}
                    </p>
                    <div className="flex gap-1 mt-2 sm:mt-0">
                        <button
                            className="px-3 py-1 border rounded disabled:opacity-50 font-bold hover:bg-gray-100 transition-all"
                            onClick={() => setPage((p) => Math.max(p - 1, 0))}
                            disabled={page === 0}
                        >
                            {t('common.previous')}
                        </button>

                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setPage(i)}
                                className={`px-3 py-1 border rounded font-bold transition-all ${page === i
                                        ? "bg-gradient-to-b from-white to-[#dcdcdc] border-[#979797] shadow-sm"
                                        : "hover:bg-gray-100"
                                    }`}
                            >
                                {i + 1}
                            </button>
                        )).slice(Math.max(0, page - 2), Math.min(totalPages, page + 3))}

                        <button
                            className="px-3 py-1 border rounded disabled:opacity-50 font-bold hover:bg-gray-100 transition-all"
                            onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
                            disabled={page === totalPages - 1 || totalPages === 0}
                        >
                            {t('common.next')}
                        </button>
                    </div>
                </div>

            </div>
        </PageFade>
    );
};

export default PaymentWorkOrderList;